import { math, SectionPlane, SectionPlanesPlugin } from "@xeokit/xeokit-sdk";
import { Eye, EyeOff, Layers, List, SquarePlus, Trash } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import {
    setSectionPlanePlugin as setGlobalSectionPlanePlugin,
    setSectionPlanes as setGlobalSectionPlanes,
    setOpenPlugin,
    toggleAddingSectionPlane,
} from "../../Context/ViewerContext/Reducer";
import { ViewerContext } from "../../Context/ViewerContext/ViewerContextProvider";
import { XktContext } from "../../Context/XktContext/XktContextProvider";

import { useTranslation } from "react-i18next";
import { Checkbox } from "../ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Separator } from "../ui/separator";
import { Toggle } from "../ui/toggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

interface XktCanvas {
    canvas: HTMLCanvasElement;
}
interface SceneWithCanvas {
    canvas: XktCanvas;
}
const SectionPlanes = () => {
    const [sectionPlanesPlugin, setSectionPlanesPlugin] = useState<SectionPlanesPlugin | null>(null);
    const { state } = useContext(XktContext);
    const { state: viewerState, dispatch, pendingSectionCreate } = useContext(ViewerContext);
    const { t } = useTranslation();
    const [selectedSectionPlane, setSelectedSectionPlane] = useState<SectionPlane | null>(null);
    const setSectionPlanes = (sectionPlanes: SectionPlane[]) => {
        dispatch(setGlobalSectionPlanes(sectionPlanes));
    };
    const lastId = useRef<number>(0);
    const active = viewerState.openPlugin === "SectionPlanes";

    useEffect(() => {
        if (viewerState.openPlugin !== "" && viewerState.openPlugin !== "SectionPlanes" && active) {
            toggleSectionPlanes(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewerState.openPlugin]);

    useEffect(() => {
        if (!state.viewer) return;
        const swc = state.viewer.scene as unknown as SceneWithCanvas;
        const mouseDown = (event: MouseEvent) => {
            if (!state.viewer) return;
            if (!viewerState.addingSectionPlane) return;
            const pickResult = state.viewer.scene.pick({
                canvasPos: [event.clientX, event.clientY],
                pickSurface: true,
            });
            if (pickResult) {
                const ent = pickResult.entity;
                if (ent) {
                    const pos = pickResult.worldPos;
                    const dir = pickResult.worldNormal;
                    createSectionPlane(pos, math.mulVec3Scalar(dir, -1));
                    dispatch(toggleAddingSectionPlane(false));
                }
            }
        };
        swc.canvas.canvas.addEventListener("mousedown", mouseDown);
        return () => {
            swc.canvas.canvas.removeEventListener("mousedown", mouseDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.viewer, viewerState.addingSectionPlane, sectionPlanesPlugin]);

    useEffect(() => {
        if (!state.viewer) return;
        const scp = new SectionPlanesPlugin(state.viewer);
        setSectionPlanesPlugin(scp);
        dispatch(setGlobalSectionPlanePlugin(scp));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.viewer]);

    const createSectionPlane = (position: number[], rotation: number[]) => {
        if (!state.viewer) return;
        if (!sectionPlanesPlugin) return;
        sectionPlanesPlugin.hideControl();
        lastId.current++;
        const id = lastId.current.toString();
        const sectionPlane = new SectionPlane(state.viewer.scene, {
            id,
            pos: position,
            dir: rotation,
            active: true,
        });

        sectionPlanesPlugin.showControl(id);
        setSectionPlanes([...viewerState.sectionPlanes, sectionPlane]);
        setSelectedSectionPlane(sectionPlane);

        if (pendingSectionCreate.current) {
            pendingSectionCreate.current.resolve(id);
            pendingSectionCreate.current = null;
        }
    };

    const selectSectionPlane = (sectionPlane: SectionPlane) => {
        setSelectedSectionPlane(sectionPlane);
        sectionPlanesPlugin?.hideControl();
        sectionPlanesPlugin?.showControl(sectionPlane.id);
    };
    const disableSectionPlane = () => {
        setSelectedSectionPlane(null);
        sectionPlanesPlugin?.hideControl();
    };

    const toggleSectionPlanes = (overwriteOpenPlugin = true) => {
        if (active) {
            if (overwriteOpenPlugin) dispatch(setOpenPlugin(""));
            if (!sectionPlanesPlugin) return;
            sectionPlanesPlugin.hideControl();
        } else {
            dispatch(setOpenPlugin("SectionPlanes"));
        }
    };

    function deSelectItem() {
        if (!sectionPlanesPlugin) return;
        sectionPlanesPlugin.showControl("");
        sectionPlanesPlugin.hideControl();
        setSelectedSectionPlane(null);
    }

    function destroyItem(item: SectionPlane) {
        deSelectItem();
        if (!sectionPlanesPlugin) return;
        sectionPlanesPlugin.destroySectionPlane(item.id);
        setSectionPlanes(viewerState.sectionPlanes.filter((i) => i.id !== item.id));
    }
    const [disabledSectionPlanes, setDisabledSectionPlanes] = useState<string[]>([]);
    const checkedChanged = (item: SectionPlane, val: boolean) => {
        item.active = val;
        if (val) {
            setDisabledSectionPlanes(disabledSectionPlanes.filter((i) => i !== item.id));
        } else {
            setDisabledSectionPlanes([...disabledSectionPlanes, item.id]);
        }
    };
    return (
        <>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex">
                        <Toggle
                            pressed={active}
                            aria-label={t("sectionPlanesAriaLabel")}
                            onPressedChange={() => {
                                if (!active) dispatch(setOpenPlugin("SectionPlanes"));
                                else {
                                    deSelectItem();
                                    dispatch(setOpenPlugin(""));
                                }
                            }}
                        >
                            <Layers className="h-4 w-4" />
                        </Toggle>
                    </span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>{t("sectionPlanes")}</p>
                </TooltipContent>
            </Tooltip>
            {viewerState.openPlugin == "SectionPlanes" && (
                <>
                    <Separator orientation="vertical" className="py-4 ml-1" />
                    <Popover open={viewerState.addingSectionPlane}>
                        <PopoverTrigger>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                        <Toggle
                                            value="italic"
                                            aria-label={t("addSectionPlaneAriaLabel")}
                                            className="ml-1 mr-1"
                                            pressed={viewerState.addingSectionPlane}
                                            onPressedChange={() => {
                                                const isAdding = viewerState.addingSectionPlane;
                                                dispatch(toggleAddingSectionPlane(!isAdding));

                                                if (isAdding && pendingSectionCreate.current) {
                                                    pendingSectionCreate.current.reject("cancelled");
                                                    pendingSectionCreate.current = null;
                                                }
                                            }}
                                        >
                                            <SquarePlus className="h-4 w-4" />
                                        </Toggle>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{t("addSectionPlane")}</p>
                                </TooltipContent>
                            </Tooltip>
                        </PopoverTrigger>
                        <PopoverContent
                            className="flex flex-col items-center mb-1 p-3"
                            style={{ width: "fit-content" }}
                        >
                            <div className="text-sm" style={{ whiteSpace: "nowrap" }}>
                                {t("clickToAddSectionPlane")}{" "}
                                <Toggle
                                    className="ml-6"
                                    pressed={viewerState.addingSectionPlane}
                                    aria-label={t("cancel")}
                                    onPressedChange={() =>
                                        dispatch(toggleAddingSectionPlane(!viewerState.addingSectionPlane))
                                    }
                                >
                                    {t("cancel")}
                                </Toggle>
                            </div>
                        </PopoverContent>
                    </Popover>

                    <Popover>
                        <PopoverTrigger>
                            <Tooltip>
                                <TooltipTrigger
                                    asChild
                                    disabled={viewerState.sectionPlanes.length === 0}
                                    aria-label={t("manageSectionPlanesAriaLabel")}
                                >
                                    <span className="inline-flex">
                                        <Toggle disabled={viewerState.sectionPlanes.length === 0} pressed={false}>
                                            <List className="h-4 w-4" />
                                        </Toggle>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{t("manageSectionPlanes")}</p>
                                </TooltipContent>
                            </Tooltip>
                        </PopoverTrigger>
                        <PopoverContent className="mb-1 p-3" align={"end"}>
                            <div className="grid gap-4">
                                {viewerState.sectionPlanes.length === 0 && <>{t("noSectionPlanes")}</>}
                                {viewerState.sectionPlanes.map((item) => (
                                    <div className="space-x-2" key={item.id}>
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                checked={!disabledSectionPlanes.includes(item.id) && item.active}
                                                onCheckedChange={(val) => checkedChanged(item, val as boolean)}
                                                aria-label={`Toggle active state for ${t("sectionPlaneId", { id: item.id })}`}
                                            />
                                            <div className="grow text-center select-none">
                                                {t("sectionPlaneId", { id: item.id })}
                                            </div>
                                            {selectedSectionPlane != item ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <EyeOff
                                                            className="cursor-pointer"
                                                            onClick={() => selectSectionPlane(item)}
                                                            aria-label={t("showDetails")}
                                                        />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>{t("showDetails")}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Eye
                                                            className="cursor-pointer"
                                                            onClick={() => disableSectionPlane()}
                                                            aria-label={t("hideDetails")}
                                                        />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>{t("hideDetails")}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}

                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Toggle
                                                        onPressedChange={() => destroyItem(item)}
                                                        aria-label={t("deleteSectionPlane")}
                                                    >
                                                        <Trash className="h-4 w-4" />
                                                    </Toggle>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{t("deleteSectionPlane")}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                </>
            )}
        </>
    );
};
export default SectionPlanes;
